import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';
import { ThemeOverridesToolInputValidationSchema } from '../theme/themeOverrides';
import {
  CreateEventPageBaseInputValidationSchema,
  UpdateEventDetailsInputValidationSchema,
} from './create';
import { UpdateFunnelStepInputValidationSchema } from '../page/common/funnel';
import {
  UpdateRedirectUrlPathSegmentValidationSchema,
  UpdateRedirectUrlQueryParameterValidationSchema,
} from '../page/common/redirectAndConfirmation';
import { UpdatePageThemeIdInputValidationSchema } from '../page/common/themeId';
import { UpdatePageFormLayoutInputValidationSchema } from '../page/formLayout';

const eventPageUpdateBodyShape = {
  ...CreateEventPageBaseInputValidationSchema.pick({
    name: true,
    title: true,
    slug: true,
    locale: true,
    description: true,
    customizeCheckoutConfirmation: true,
    status: true,
    testmode: true,
    allowDynamicTitle: true,
    allowDynamicDescription: true,
    closePopupOnClickOutside: true,
    hostedLayoutType: true,
    themeId: true,
    themeOverrides: true,
    redirect: true,
    afterPaymentAction: true,
    confirmationCheckoutTitle: true,
    confirmationCheckoutMessage: true,
    redirectUrl: true,
    redirectUrlInsideEmbed: true,
    redirectPageId: true,
    sendPaymentNotification: true,
    notifyEmail: true,
    sendEmailConfirmation: true,
    customizeEmailConfirmation: true,
    confirmationEmailSubject: true,
    confirmationEmailMessage: true,
    confirmationEmailShowLogo: true,
    confirmationEmailShowStoreName: true,
    googleIndex: true,
    trackingCodes: true,
    invoiceSettings: true,
    savePaymentMethod: true,
    paymentMethods: true,
    checkoutAbandonment: true,
    paymentOptions: true,
    fees: true,
    coverProcessingFees: true,
    showCouponCodeField: true,
    showCouponCodeFieldType: true,
    allowDynamicPrice: true,
    allowDynamicDiscountedFromPrice: true,
    allowDynamicLabel: true,
    allowDynamicPreselectedPrice: true,
    allowDynamicRedirectUrl: true,
    imageIds: true,
    tax: true,
    eventDetails: true,
    showConfirmationEventName: true,
    showConfirmationEventDateTime: true,
    showConfirmationEventLocation: true,
    showConfirmationEventMeetingLink: true,
    showConfirmationEventTickets: true,
    showConfirmationAddToCalendar: true,
    showConfirmationEventAddOns: true,
    funnelSteps: true,
    redirectUrlPath: true,
    redirectUrlQuery: true,
  }).partial().extend({
    themeId: UpdatePageThemeIdInputValidationSchema,
    formLayout: UpdatePageFormLayoutInputValidationSchema.optional(),
    // Update can't create ticket types (ticketGroups is excluded from this
    // body), so discounts[].ticketTypeIds must reference existing types by
    // {id} — the create-time {key} form has nothing to resolve against.
    eventDetails: UpdateEventDetailsInputValidationSchema.nullish().meta({
      description:
        'Event-specific details like type, dates, location. Ticket-specific discounts (`discounts[].ticketTypeIds`) must reference existing ticket types by `{ id }` — update cannot create new ticket types to key against, so the create-time `{ key }` form is rejected.',
    }),
    // Same reasoning for field references inside funnel steps and redirect
    // path/query segments — fields is excluded from this body, so they must
    // resolve by {fieldId}, not the create-time {fieldKey}.
    funnelSteps: z.array(UpdateFunnelStepInputValidationSchema).nullish().meta({
      description:
        'Ordered funnel configuration for this page. Supports intermediate checkout steps, one-click upsells, and confirmation behavior. Field references inside upsell/confirmation redirect steps resolve by `fieldId` (get current field IDs from the page detail response) — the create-time `fieldKey` string is rejected.',
    }),
    redirectUrlPath: z.array(UpdateRedirectUrlPathSegmentValidationSchema).nullish().meta({
      description:
        'Additional path parameters appended to the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. To reference a field value, use `fieldId` (the persisted field ID, from the page detail response) instead of the create-time `fieldKey`. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [
        { key: 'fields', fieldId: '507f1f77bcf86cd799439011' },
        { key: 'orderId', identifier: 'orderId' },
      ],
    }),
    redirectUrlQuery: z.array(UpdateRedirectUrlQueryParameterValidationSchema).nullish().meta({
      description:
        'Query parameters for the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. To reference a field value, use `fieldId` instead of the create-time `fieldKey`. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [{ parameter: 'name', key: 'fields', fieldId: '507f1f77bcf86cd799439011' }],
    }),
  }).shape,
};

/**
 * The canonical list of fields a merchant may PATCH on an event page.
 * Derived from `eventPageUpdateBodyShape` so the API controller and the MCP
 * tool can iterate the same list without re-declaring it (drift-proof — see
 * `apps/api/src/pages/schemas/eventPageUpdateFields.driftGuard.test.ts`).
 * `ticketGroups` and `fields` are intentionally excluded — they're managed
 * via their own endpoints, not this PATCH body.
 */
export const EVENT_PAGE_UPDATE_FIELDS = Object.keys(
  eventPageUpdateBodyShape,
) as ReadonlyArray<keyof typeof eventPageUpdateBodyShape>;

/**
 * Unrefined, non-strict base for the update body. The API's PATCH endpoint
 * builds on this (it stays lenient); the MCP body below layers strict() and
 * the at-least-one-field refine on top.
 */
export const EventPageUpdateBodyBaseValidationSchema = z.object(eventPageUpdateBodyShape);

export const EventPageUpdateBodyValidationSchema = z
  .object(eventPageUpdateBodyShape)
  .strict()
  .refine(hasAtLeastOneField, {
    message: 'At least one field must be provided.',
  });

// MCP update tool input; themeOverrides is loose, see ThemeOverridesToolInputValidationSchema.
export const UpdateEventPageInputValidationSchema = z
  .object({
    pageId: ObjectIdStringValidationSchema.meta({
      description: 'The ID of the event page to update.',
    }),
    ...eventPageUpdateBodyShape,
    themeOverrides: ThemeOverridesToolInputValidationSchema,
  })
  .refine(
    value => {
      const { pageId: _pageId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one update field must be provided.' },
  );

export type UpdateEventPageInput = z.infer<typeof UpdateEventPageInputValidationSchema>;
